import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Button, Modal } from '@safetyhub/ui';
import { isKebabKey, KEBAB_KEY_ERROR } from './keyValidation';

type TemplateKeyModalProps = {
  /** 모달 열림 여부. false면 렌더 안 함. */
  open: boolean;
  /** 헤더 제목. */
  title: string;
  /** 확인 버튼 라벨. */
  confirmLabel: string;
  /** 열릴 때 입력 초기값(비우려면 ''). */
  defaultKey: string;
  /** 이미 존재하는 template key 목록 — 중복 key 인라인 검증에 사용. */
  existingKeys: string[];
  /** 요청 진행 중 — 확인 버튼 비활성(중복 클릭 방지). */
  busy: boolean;
  /** 입력 라벨. 기본 'key'. */
  keyLabel?: string;
  /** 하단 예시 힌트(오류 없을 때 '예: {hintExample}'로 표시). */
  hintExample?: string;
  /** 미저장 변경 경고 등 amber 강조 안내(있을 때만 입력 위에 표시). */
  warning?: string;
  /** 입력 위(warning 위)에 렌더할 상단 정보 슬롯 — 복제 모달의 "원본: {name} ({key})" 등. */
  topSlot?: ReactNode;
  /** 확인 — 검증 통과한 key를 전달. */
  onConfirm: (key: string) => void;
  onClose: () => void;
};

/**
 * 템플릿 key 입력 모달 — 네이티브 window.prompt 대체(신규 템플릿 key 입력 등).
 * 오버레이·z-index(z-[70])·Esc/오버레이 닫기·포커스 트랩은 공용 [[Modal]]이 소유한다.
 * - key는 kebab-case(소문자/숫자/하이픈) + 빈값/중복 시 확인 비활성(인라인 오류 표시).
 * - warning이 있으면(예: 미저장 변경) amber로 강조해 별도 confirm 단계 없이 안내를 통합한다.
 * - 오버레이/Esc/취소=onClose, 확인=onConfirm. Enter는 오조작 방지 위해 미할당.
 * - 열림 시 포커스는 key 입력으로(base의 initialFocusRef — autoFocus는 base 자동 포커스와 경합).
 */
export function TemplateKeyModal({
  open,
  title,
  confirmLabel,
  defaultKey,
  existingKeys,
  busy,
  keyLabel = 'key',
  hintExample,
  warning,
  topSlot,
  onConfirm,
  onClose,
}: TemplateKeyModalProps) {
  const [key, setKey] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // 열릴 때마다 기본값으로 초기화(직전 입력 잔존 방지).
  useEffect(() => {
    if (open) setKey(defaultKey);
  }, [open, defaultKey]);

  const trimmed = key.trim();
  const error = useMemo(() => {
    if (!trimmed) return null; // 빈값은 오류 문구 없이 확인만 비활성.
    if (!isKebabKey(trimmed)) return KEBAB_KEY_ERROR;
    if (existingKeys.includes(trimmed)) return `이미 존재하는 key입니다: ${trimmed}`;
    return null;
  }, [trimmed, existingKeys]);

  const canConfirm = !!trimmed && !error && !busy;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      initialFocusRef={inputRef}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button variant="primary" onClick={() => onConfirm(trimmed)} disabled={!canConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {topSlot}
        {warning ? (
          <p className="text-xs font-medium leading-relaxed text-amber-600">{warning}</p>
        ) : null}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="template-key-input">
            {keyLabel}
          </label>
          <input
            id="template-key-input"
            ref={inputRef}
            type="text"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="kebab-case (소문자/숫자/하이픈)"
            aria-invalid={!!error || undefined}
            className={`w-full rounded border px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 ${
              error
                ? 'border-red-400 focus:ring-red-200'
                : 'border-gray-300 focus:ring-brand-200'
            }`}
          />
          {error ? (
            <p className="mt-1 text-xs text-red-600">{error}</p>
          ) : hintExample ? (
            <p className="mt-1 text-xs text-gray-400">예: {hintExample}</p>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
